import * as CollapsiblePrimitive from '@radix-ui/react-collapsible';
import { motion } from 'motion/react';
import * as React from 'react';

import { cn } from '../../utils/cn';

const CollapsibleContentGossipContext = React.createContext<number | undefined>(undefined);

const Collapsible = React.forwardRef<
  React.ElementRef<typeof CollapsiblePrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof CollapsiblePrimitive.Root> & {
    collapsibleContentGossip?: number;
  }
>(({ collapsibleContentGossip, ...props }, ref) => (
  <CollapsibleContentGossipContext.Provider value={collapsibleContentGossip}>
    <CollapsiblePrimitive.Root ref={ref} {...props} />
  </CollapsibleContentGossipContext.Provider>
));
Collapsible.displayName = CollapsiblePrimitive.Root.displayName;

const CollapsibleTrigger = CollapsiblePrimitive.CollapsibleTrigger;

const CollapsibleContent = React.forwardRef<
  React.ElementRef<typeof CollapsiblePrimitive.CollapsibleContent>,
  React.ComponentPropsWithoutRef<typeof CollapsiblePrimitive.CollapsibleContent>
>(({ className, children, ...props }, ref) => {
  const collapsibleContentGossip = React.useContext(CollapsibleContentGossipContext);
  const contentRef = React.useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = React.useState(false);

  const setRefs = React.useCallback(
    (node: HTMLDivElement | null) => {
      contentRef.current = node;

      if (typeof ref === 'function') {
        ref(node);
        return;
      }

      if (ref) {
        ref.current = node;
      }
    },
    [ref],
  );

  React.useEffect(() => {
    const node = contentRef.current;

    if (!node) {
      return;
    }

    const syncState = () => {
      setIsOpen(node.getAttribute('data-state') === 'open');
    };

    syncState();

    const observer = new MutationObserver(syncState);
    observer.observe(node, { attributes: true, attributeFilter: ['data-state'] });

    return () => observer.disconnect();
  }, []);

  return (
    <CollapsiblePrimitive.CollapsibleContent
      ref={setRefs}
      forceMount
      {...props}
      className="relative overflow-hidden px-1"
    >
      <motion.div
        animate={isOpen ? 'open' : 'closed'}
        initial={false}
        transition={{ duration: 0.2, ease: 'easeInOut' }}
        variants={{
          open: { height: 'auto', opacity: 1, y: 0 },
          closed:
            collapsibleContentGossip !== undefined
              ? { height: `${collapsibleContentGossip}rem`, opacity: 1, y: 0 }
              : { height: 0, opacity: 0, y: -6 },
        }}
      >
        <div className={cn(className)}>{children}</div>
      </motion.div>
      {collapsibleContentGossip !== undefined && collapsibleContentGossip > 0 && !isOpen && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 bottom-0 h-2 bg-linear-to-t from-black/10 to-transparent"
        />
      )}
    </CollapsiblePrimitive.CollapsibleContent>
  );
});
CollapsibleContent.displayName = CollapsiblePrimitive.CollapsibleContent.displayName;

export { Collapsible, CollapsibleContent, CollapsibleTrigger };
